/**
 * ResourceManager - loading and caching of resources (materials, models, sounds, JSON objects).
 * Note: Texture loading has been moved to TextureManager.ts.
 */

import * as THREE from 'three';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { type GLTF, GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { USDLoader } from 'three/addons/loaders/USDLoader.js';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';

import { MaterialUtils } from '../materials.js';
import { NetRuntime } from '../multiplayer/NetRuntime.js';
import { Renderer } from '../render/Renderer.js';
import { setUrlTextureLoader } from '../render/Texture.js';
import { projectInfo, resolvePath } from '../storage/projectContext.js';
import { AssetPath } from '../storage/storageProvider.js';
import { AbstractEngineClass, ClassRegistry, Prefix } from '../systems/ClassRegistry.js';
import { ObjectTracker } from '../systems/ObjectTracker.js';
import { WorldSerializer } from '../systems/WorldSerialization.js';
import { TextureManager } from '../utils/TextureManager.js';
import { isNode } from '../utils/Utils.js';


import { sanitizeGltfLoadedModel } from './sanitizeGltfGeometryForGpu.js';

import type { GenericMaterialType } from '../materials.js';

export enum ResourceType {
  Material = 1 << 0,
  Model = 1 << 1,
  Texture = 1 << 2,
  Sound = 1 << 3,
  JsonObject = 1 << 4,
  Resource = 1 << 5,
  All = 0xFFFFFFFF,
}

/**
 * Base class for custom resource types.
 * Resources are serializable objects that can be created and edited in the editor.
 *
 * By default, resources are saved with the '.res.json' extension.
 * You can customize the file prefix by providing a `resourcePrefix` option to the decorator.
 *
 * @example
 * ```ts
 * // Default resource - saves as MyResource.res.json
 * @GameClass({ isResource: true })
 * class MyResource extends Resource {
 *   @property()
 *   myProperty: string = '';
 * }
 *
 * // Custom prefix - saves as MyMaterial.material.json
 * @GameClass({ isResource: true, resourcePrefix: 'material' })
 * class CustomMaterial extends Resource {
 *   @property({ type: 'color' })
 *   color: string = '#ffffff';
 *
 *   @property({ type: 'number', min: 0, max: 1 })
 *   roughness: number = 0.5;
 * }
 *
 * // Shader resource - saves as MyShader.shader.json
 * @GameClass({ isResource: true, resourcePrefix: 'shader' })
 * class ShaderConfig extends Resource {
 *   @property()
 *   vertexShader: string = '';
 *
 *   @property()
 *   fragmentShader: string = '';
 * }
 * ```
 */
@AbstractEngineClass('Resource')
export class Resource {
  /**
   * Returns the icon name for this resource class, used by the editor.
   * The name maps to a PNG file under `/.icons/objects/<name>.png` in the editor's public folder.
   * Override in subclasses to provide a custom icon.
   *
   * @returns An icon name (e.g. `'Icon_MyResource'`), or `null` to use the default resource icon.
   */
  public getEditorClassIcon(): string | null {
    return 'Icon_Resource';
  }
}

export interface ResourceData<T extends Resource = Resource> {
  class: string;
  instance: T;
}

interface GenesysMaterialOverrideExtension {
  materialOverride?: string;
  materialBindingKey?: string;
}

interface MaterialOverrideEntry {
  key: string;
  path: string;
}

/**
 * Manages the loading and caching of resources.
 */
export class ResourceManager {
  protected cachedMaterials: Record<string, Promise<THREE.Material | null>> = {};

  // Models with material overrides applied, may contain the same model as rawCachedModels if it contains no material override
  protected cachedModels: Record<string, Promise<GLTF | null>> = {};
  // Models without material overrides applied
  protected rawCachedModels: Record<string, Promise<GLTF | null>> = {};
  protected cachedSounds: Record<string, Promise<AudioBuffer | null>> = {};
  protected cachedJsonObjects: Record<string, Promise<any | null>> = {};
  protected cachedResources: Record<string, Promise<ResourceData | null>> = {};

  protected gltfLoader: GLTFLoader = new GLTFLoader();
  protected usdLoader: USDLoader = new USDLoader();
  protected audioLoader: THREE.AudioLoader = new THREE.AudioLoader();
  protected textureManager: TextureManager = new TextureManager();

  /**
   * GPU resources the cache owns and disposes (via {@link clearCachedResources}). Shared
   * across many objects (path-keyed cache + `SkeletonUtils.clone`), so actor/component
   * teardown must NOT dispose anything in here. @see {@link isCacheOwned}
   */
  private readonly cacheOwned: WeakSet<object> = new WeakSet();

  constructor() {
    ObjectTracker.track(this);
    if (!isNode()) {
      this.gltfLoader.setCrossOrigin('anonymous');
      this.usdLoader.setCrossOrigin('anonymous');
    }

    // Set up Meshopt decoder for compressed GLTF files
    this.gltfLoader.setMeshoptDecoder(MeshoptDecoder);

    // Set up DRACO decoder for DRACO-compressed GLTF files
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
    this.gltfLoader.setDRACOLoader(dracoLoader);
  }

  /**
   * Gets the cached materials.
   *
   * @returns The cached materials
   */
  public getCachedMaterials(): Record<string, Promise<THREE.Material | null>> {
    return this.cachedMaterials;
  }

  /**
   * Gets the cached models.
   *
   * @returns The cached models
   */
  public getCachedModels(): Record<string, Promise<GLTF | null>> {
    return this.cachedModels;
  }

  /**
   * Gets the cached textures.
   *
   * @returns The cached textures
   */
  public getCachedTextures(): Record<string, Promise<THREE.Texture | null>> {
    return this.textureManager.getCachedTextures();
  }

  /**
   * Gets the cached sounds.
   *
   * @returns The cached sounds
   */
  public getCachedSounds(): Record<string, Promise<AudioBuffer | null>> {
    return this.cachedSounds;
  }

  /**
   * Gets the cached json objects.
   *
   * @returns The cached json objects
   */
  public getCachedJsonObjects(): Record<string, Promise<any | null>> {
    return this.cachedJsonObjects;
  }

  /**
   * Gets the cached resources.
   *
   * @returns The cached resources
   */
  public getCachedResources(): Record<string, Promise<ResourceData | null>> {
    return this.cachedResources;
  }

  /**
   * @returns `true` if `resource` is owned by this cache (must not be disposed by
   * per-actor teardown). Use as the `isProtected` predicate for {@link disposeObject3DSubtree}.
   */
  public isCacheOwned(resource: object): boolean {
    return this.cacheOwned.has(resource);
  }

  /** Marks a resource as cache-owned so subtree disposal skips it. Idempotent. */
  public markCacheOwned(resource: object | null | undefined): void {
    if (resource) {
      this.cacheOwned.add(resource);
    }
  }

  /** Marks a material plus every texture-valued property it references as cache-owned. */
  private markMaterialOwned(material: THREE.Material | null | undefined): void {
    if (!material) {
      return;
    }
    this.markCacheOwned(material);
    for (const value of Object.values(material)) {
      if (value instanceof THREE.Texture) {
        this.markCacheOwned(value);
      }
    }
  }

  /**
   * Marks a model's geometries/materials/textures as cache-owned. `SkeletonUtils.clone`
   * shares these refs, so marking the source covers every clone.
   */
  private markModelOwned(gltf: GLTF | null | undefined): void {
    if (!gltf) {
      return;
    }
    const visit = (root: THREE.Object3D) => {
      root.traverse((object) => {
        const mesh = object as THREE.Mesh;
        if (!(mesh as unknown as { isMesh?: boolean }).isMesh) {
          return;
        }
        this.markCacheOwned(mesh.geometry);
        if (mesh.material) {
          const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          for (const material of materials) {
            this.markMaterialOwned(material);
          }
        }
      });
    };
    if (gltf.scene) {
      visit(gltf.scene);
    }
    for (const scene of gltf.scenes ?? []) {
      if (scene !== gltf.scene) {
        visit(scene);
      }
    }
  }

  /**
   * Clears the cached resources and disposes of GPU-backed objects (materials,
   * geometries, textures) so their WebGL/WebGPU handles are released.
   *
   * Three.js GPU resources are not reclaimed by the JavaScript garbage
   * collector — only `dispose()` releases the underlying buffers and shader
   * programs. We therefore:
   *   1. Snapshot the current promise caches and reset the slots immediately
   *      so subsequent loads start with a fresh cache.
   *   2. Asynchronously await each cached promise (including in-flight loads)
   *      and dispose the resolved value, deduping shared references via a Set
   *      to avoid double-dispose between cloned and raw GLTF models or
   *      between models and their override materials.
   *
   * Pending loads that started before this call will still complete and be
   * disposed; loads queued after this call go into the new cache.
   */
  public clearCachedResources(): void {
    const materials = this.cachedMaterials;
    const models = this.cachedModels;
    const rawModels = this.rawCachedModels;
    const resources = this.cachedResources;

    this.cachedMaterials = {};
    this.cachedModels = {};
    this.rawCachedModels = {};
    this.cachedSounds = {};
    this.cachedJsonObjects = {};
    this.cachedResources = {};
    // Delegate to TextureManager for texture cache
    this.textureManager.clearCache();

    void this.disposeSnapshot({ materials, models, rawModels, resources });
  }

  /**
   * Awaits the snapshotted promises and disposes all GPU-backed resolved
   * values. A shared `seen` Set dedupes shared references (e.g. a material
   * referenced by multiple GLTFs, or geometries shared between a raw GLTF and
   * its `SkeletonUtils.clone`).
   */
  private async disposeSnapshot(snapshot: {
    materials: Record<string, Promise<THREE.Material | null>>;
    models: Record<string, Promise<GLTF | null>>;
    rawModels: Record<string, Promise<GLTF | null>>;
    resources: Record<string, Promise<ResourceData | null>>;
  }): Promise<void> {
    const seen = new Set<object>();
    const settle = <T>(p: Promise<T | null>): Promise<T | null> => p.catch(() => null);

    try {
      const [materials, rawModels, models, resources] = await Promise.all([
        Promise.all(Object.values(snapshot.materials).map(settle)),
        Promise.all(Object.values(snapshot.rawModels).map(settle)),
        Promise.all(Object.values(snapshot.models).map(settle)),
        Promise.all(Object.values(snapshot.resources).map(settle)),
      ]);

      for (const mat of materials) ResourceManager.disposeMaterial(mat, seen);
      for (const gltf of rawModels) ResourceManager.disposeGltf(gltf, seen);
      for (const gltf of models) ResourceManager.disposeGltf(gltf, seen);
      for (const res of resources) {
        if (!res) continue;
        const inst = res.instance as unknown;
        if (inst instanceof THREE.Material) ResourceManager.disposeMaterial(inst, seen);
        else if (inst instanceof THREE.Texture) ResourceManager.disposeTexture(inst, seen);
      }
    } catch (error) {
      console.warn('[ResourceManager] Error while disposing cached resources', error);
    }
  }

  /** Disposes a texture exactly once. */
  private static disposeTexture(tex: THREE.Texture | null | undefined, seen: Set<object>): void {
    if (!tex || seen.has(tex)) return;
    seen.add(tex);
    tex.dispose();
  }

  /**
   * Disposes a material and all texture-valued properties it owns. Three.js
   * `Material.dispose()` does not free its texture maps because textures are
   * frequently shared between materials, so we walk the property bag and
   * dispose each `THREE.Texture` reference (deduped via `seen`).
   */
  private static disposeMaterial(mat: THREE.Material | null | undefined, seen: Set<object>): void {
    if (!mat || seen.has(mat)) return;
    seen.add(mat);
    for (const key of Object.keys(mat)) {
      const value = (mat as unknown as Record<string, unknown>)[key];
      if (value instanceof THREE.Texture) {
        ResourceManager.disposeTexture(value, seen);
      }
    }
    mat.dispose();
  }

  /**
   * Traverses a GLTF scene graph and disposes geometries plus mesh materials
   * (including their texture maps). Cloned GLTFs share geometry/material
   * references with their raw source, so the shared `seen` Set is required to
   * avoid redundant work.
   */
  private static disposeGltf(gltf: GLTF | null | undefined, seen: Set<object>): void {
    if (!gltf || seen.has(gltf)) return;
    seen.add(gltf);
    const visit = (root: THREE.Object3D) => {
      root.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (!(mesh as unknown as { isMesh?: boolean }).isMesh) return;
        const geom = mesh.geometry as THREE.BufferGeometry | undefined;
        if (geom && !seen.has(geom)) {
          seen.add(geom);
          geom.dispose();
        }
        if (mesh.material) {
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          for (const m of mats) ResourceManager.disposeMaterial(m, seen);
        }
      });
    };
    if (gltf.scene) visit(gltf.scene);
    for (const scene of gltf.scenes ?? []) {
      if (scene !== gltf.scene) visit(scene);
    }
  }

  /**
   * Invalidates cached models that may have changed material overrides.
   * Call this when material overrides have been modified.
   *
   * @param modelPath - Optional specific model path to invalidate. If not provided, clears all model cache.
   * @param clearRawCache - Whether to clear the raw (non-material-applied) cache
   * @param clearMaterialCache - Whether to clear the material-applied cache
   */
  public invalidateModelCache(modelPath?: AssetPath | string, clearRawCache: boolean = true, clearMaterialCache: boolean = true): void {
    if (modelPath) {
      const pathStr = typeof modelPath === 'string' ? modelPath : modelPath.initialPath;
      if (clearMaterialCache) {
        delete this.cachedModels[pathStr];
      }
      if (clearRawCache) {
        delete this.rawCachedModels[pathStr];
      }
    } else {
      if (clearMaterialCache) {
        this.cachedModels = {};
      }
      if (clearRawCache) {
        this.rawCachedModels = {};
      }
    }
  }

  /**
   * Invalidates cached resources.
   * Call this when resources have been modified and need to be reloaded.
   *
   * @param resourcePath - Optional specific resource path to invalidate. If not provided, clears all resource cache.
   */
  public invalidateResourceCache(resourcePath?: AssetPath | string): void {
    if (resourcePath) {
      const pathStr = typeof resourcePath === 'string' ? resourcePath : resourcePath.initialPath;
      delete this.cachedResources[pathStr];
    } else {
      this.cachedResources = {};
    }
  }

  /**
   * Invalidates cached materials.
   * Call this when materials have been modified and need to be reloaded.
   *
   * @param materialPath - Optional specific material path to invalidate. If not provided, clears all material cache.
   */
  public invalidateMaterialCache(materialPath?: AssetPath | string): void {
    if (materialPath) {
      const pathStr = typeof materialPath === 'string' ? materialPath : materialPath.initialPath;
      delete this.cachedMaterials[pathStr];
    } else {
      this.cachedMaterials = {};
    }
  }

  /**
   * Invalidates cached textures.
   * Call this when textures have been modified and need to be reloaded.
   *
   * @param texturePath - Optional specific texture path to invalidate. If not provided, clears all texture cache.
   */
  public invalidateTextureCache(texturePath?: AssetPath | string): void {
    this.textureManager.invalidateCache(texturePath);
  }

  /**
   * Invalidates cached JSON objects.
   * Call this when JSON assets have been modified and need to be reloaded.
   *
   * @param jsonPath - Optional specific JSON path(s) to invalidate. If not provided, clears all JSON object cache.
   */
  public invalidateJsonObjectCache(jsonPath?: AssetPath | string | Array<AssetPath | string>): void {
    if (!jsonPath) {
      this.cachedJsonObjects = {};
      return;
    }

    const paths = Array.isArray(jsonPath) ? jsonPath : [jsonPath];
    for (const path of paths) {
      const pathStr = typeof path === 'string' ? path : path.initialPath;
      delete this.cachedJsonObjects[pathStr];
    }
  }

  /** Logs the cached resources. */
  public logCachedResources(): void {
    console.log('[ResourceManager] cached materials:', Object.keys(this.cachedMaterials));
    console.log('[ResourceManager] cached models:', Object.keys(this.cachedModels));
    console.log('[ResourceManager] raw cached models:', Object.keys(this.rawCachedModels));
    console.log('[ResourceManager] cached textures:', Object.keys(this.textureManager.getCachedTextures()));
    console.log('[ResourceManager] cached sounds:', Object.keys(this.cachedSounds));
    console.log('[ResourceManager] cached json objects:', Object.keys(this.cachedJsonObjects));
    console.log('[ResourceManager] cached resources:', Object.keys(this.cachedResources));
  }

  /**
   * Wait for arbitrary combination of resource types to load.
   *
   * @param resourceTypes - The resource types to wait for
   */
  public async waitForResources(resourceTypes: number) {
    let numPromises = 0;
    while (true) {
      let promises: Promise<any | null>[] = [];
      if (resourceTypes & ResourceType.Texture) {
        promises = promises.concat(this.textureManager.getPendingPromises());
      }
      if (resourceTypes & ResourceType.Model) {
        promises = promises.concat(Object.values(this.cachedModels));
        promises = promises.concat(Object.values(this.rawCachedModels));
      }
      if (resourceTypes & ResourceType.Sound) {
        promises = promises.concat(Object.values(this.cachedSounds));
      }
      if (resourceTypes & ResourceType.JsonObject) {
        promises = promises.concat(Object.values(this.cachedJsonObjects));
      }
      if (resourceTypes & ResourceType.Material) {
        promises = promises.concat(Object.values(this.cachedMaterials));
      }
      if (resourceTypes & ResourceType.Resource) {
        promises = promises.concat(Object.values(this.cachedResources));
      }
      // this handles the case where new resources are added to the system while waiting for the current iteration to finish
      // in that case we'll run the loop again to wait for the new ones, until there's none left
      if (numPromises === promises.length) {
        break;
      }
      numPromises = promises.length;
      await Promise.all(promises);
    }
  }

  /**
   * Loads a texture from a URL.
   * Delegates to TextureManager.
   *
   * @param path - The path of the texture to load
   * @returns The loaded texture, or null if the texture could not be loaded
   */
  public async loadTexture(path: AssetPath): Promise<THREE.Texture | null> {
    const texture = await this.textureManager.loadTexture(path);
    this.markCacheOwned(texture);
    return texture;
  }

  /**
   * Initializes the KTX2 loader for loading KTX2 compressed textures.
   * Must be called before loading any KTX2 textures.
   *
   * @param renderer - The Renderer to use for detecting KTX2 support
   */
  public initWithRenderer(renderer: Renderer): void {
    this.textureManager.initWithRenderer(renderer);

    // Set up KTX2 loader from TextureManager for GLTF models with KTX2 textures
    const ktx2Loader = this.textureManager.ktx2Loader;
    if (ktx2Loader) {
      this.gltfLoader.setKTX2Loader(ktx2Loader);
    }
  }

  private static getMaterialOverrideExtension(material: THREE.Material): GenesysMaterialOverrideExtension | null {
    const extension = material.userData?.gltfExtensions?.GENESYS_material_overrides;
    if (!extension || typeof extension !== 'object') {
      return null;
    }
    return extension as GenesysMaterialOverrideExtension;
  }

  private static getMaterialOverridePath(material: THREE.Material, mesh: THREE.Mesh): string | null {
    const extension = ResourceManager.getMaterialOverrideExtension(material);
    if (typeof extension?.materialOverride === 'string' && extension.materialOverride.trim().length > 0) {
      return extension.materialOverride.trim();
    }

    // Legacy fallback: mesh-level map keyed by material UUID.
    const meshOverrides = mesh.userData?.gltfExtensions?.GENESYS_material_overrides;
    if (!meshOverrides || typeof meshOverrides !== 'object') {
      return null;
    }

    const legacyOverride = (meshOverrides as Record<string, unknown>)[material.uuid];
    if (typeof legacyOverride === 'string' && legacyOverride.trim().length > 0) {
      return legacyOverride.trim();
    }

    return null;
  }

  public static getMaterialOverrideEntries(gltf: GLTF): MaterialOverrideEntry[] {
    const entries: MaterialOverrideEntry[] = [];
    let fallbackIndex = 0;

    gltf.scene.traverse(object => {
      if (!(object instanceof THREE.Mesh) || !object.material) {
        return;
      }
      const materials = Array.isArray(object.material) ? object.material : [object.material];

      materials.forEach(material => {
        const overridePath = ResourceManager.getMaterialOverridePath(material, object);
        if (!overridePath) {
          return;
        }

        const extension = ResourceManager.getMaterialOverrideExtension(material);
        const bindingKey = typeof extension?.materialBindingKey === 'string' && extension.materialBindingKey.trim().length > 0
          ? extension.materialBindingKey.trim()
          : null;
        const materialName = material.name?.trim();
        entries.push({
          key: bindingKey ?? materialName ?? `material-slot:${fallbackIndex}`,
          path: overridePath,
        });
        fallbackIndex++;
      });
    });

    return entries;
  }

  /**
   * Extracts material overrides from individual materials in the GLTF.
   *
   * @param gltf - The loaded GLTF object
   * @returns Record of material identity to asset path mappings
   */
  public static getMaterialOverrides(gltf: GLTF): Record<string, string> {
    const overrides: Record<string, string> = {};
    const keyCounts = new Map<string, number>();

    for (const entry of ResourceManager.getMaterialOverrideEntries(gltf)) {
      const currentCount = keyCounts.get(entry.key) ?? 0;
      keyCounts.set(entry.key, currentCount + 1);
      const dedupedKey = currentCount > 0 ? `${entry.key}#${currentCount + 1}` : entry.key;
      overrides[dedupedKey] = entry.path;
    }

    return overrides;
  }


  /**
   * Applies material overrides to a loaded GLTF model.
   * @param gltf - The loaded GLTF object.
   */
  private async applyMaterialOverrides(gltf: GLTF): Promise<void> {
    const overrideEntries = ResourceManager.getMaterialOverrideEntries(gltf);
    const overridePaths = Array.from(new Set(overrideEntries.map(entry => entry.path)));

    // Load all override materials first (batch loading for performance)
    const materialPromises = overridePaths.map(async materialPath => {
      try {
        const material = await this.loadMaterial(AssetPath.fromString(materialPath));
        return { path: materialPath, material };
      } catch (error) {
        console.warn(`[ResourceManager] Failed to load override material: ${materialPath}`, error);
        return { path: materialPath, material: null };
      }
    });

    const loadedMaterials = await Promise.all(materialPromises);
    const materialCache = new Map(
      loadedMaterials
        .filter(result => result.material !== null)
        .map(result => [result.path, result.material])
    );

    // Apply overrides to meshes
    gltf.scene.traverse((object) => {
      if (object instanceof THREE.Mesh && object.material) {
        const materials = Array.isArray(object.material) ? object.material : [object.material];

        const newMaterials = materials.map((material) => {
          const overridePath = ResourceManager.getMaterialOverridePath(material, object);

          if (overridePath) {
            const overrideMaterial = materialCache.get(overridePath);
            if (overrideMaterial) {
              return overrideMaterial;
            } else {
              const materialLabel = material.name && material.name.length > 0
                ? material.name
                : material.uuid;
              console.warn(`[ResourceManager] Override material not found for "${materialLabel}": ${overridePath}`);
            }
          }

          return material; // Return original if no override or override failed
        });

        // Update the mesh material(s)
        object.material = Array.isArray(object.material) ? newMaterials : newMaterials[0];
      }
    });
  }

  private async loadRawModel(path: AssetPath): Promise<GLTF | null> {
    const rawUrl = path.initialPath;

    if (rawUrl in this.rawCachedModels) {
      return this.rawCachedModels[rawUrl];
    }

    const loadingPromise = this.loadModelFromStorageProvider(AssetPath.fromString(rawUrl));
    this.rawCachedModels[rawUrl] = loadingPromise;
    return loadingPromise;
  }

  private async loadModelWithMaterialsApplied(path: AssetPath): Promise<GLTF | null> {
    const rawUrl = path.initialPath;

    if (rawUrl in this.cachedModels) {
      return this.cachedModels[rawUrl];
    }

    // This will load if required, and handle caching for the raw assets.
    let rawModel = await this.loadRawModel(path);

    if (rawModel) {
      const materialOverrides = ResourceManager.getMaterialOverrideEntries(rawModel);
      if (materialOverrides.length > 0) {
        // Deep clone to avoid modifying the raw cached version
        const clonedModel = this.CloneModel(rawModel);
        await this.applyMaterialOverrides(clonedModel);
        this.cachedModels[rawUrl] = Promise.resolve(clonedModel);
        return clonedModel;
      } else {
        // avoid cloning the raw model unnecessarily
        this.cachedModels[rawUrl] = Promise.resolve(rawModel);
        return rawModel;
      }
    }

    return null;
  }

  private async loadModelFromStorageProvider(path: AssetPath): Promise<GLTF | null> {
    if (isNode()) {
      return  {
        animations: [],
        scene: new THREE.Group(),
        scenes: [],
        cameras: [],
        asset: {
        },
        parser: {} as any,
        userData: {}
      };
    }
    return(async () => {
      try {
        const storageProvider = projectInfo.storageProvider;
        if (!storageProvider) {
          throw new Error('No storage provider found');
        }
        const arrayBuffer = await storageProvider.downloadFileAsBuffer(path);
        const resolvedPath = await storageProvider.resolvePath(path);
        const filePath = resolvedPath.getResolvedPath();

        // Parse URL to get pathname without query params
        let model: GLTF | null = null;
        const url = new URL(filePath, 'file://');
        const pathname = url.pathname.toLowerCase();
        if (pathname.endsWith('.usd') || pathname.endsWith('.usda') ||
            pathname.endsWith('.usdc') || pathname.endsWith('.usdz')) {
          // USD format
          const scene = await this.usdLoader.parse(arrayBuffer);
          model = {
            animations: [],
            scene,
            scenes: [],
            cameras: [],
            asset: {},
            parser: {} as any,
            userData: {},
          };
        } else {
          // GLTF/GLB format (default)
          const baseUrl = THREE.LoaderUtils.extractUrlBase(resolvedPath.getResolvedPath());
          model = await this.gltfLoader.parseAsync(arrayBuffer, baseUrl);
        }
        if (model) {
          sanitizeGltfLoadedModel(model);
          // Fix texture color spaces at load time so the per-frame render callback is unnecessary
          MaterialUtils.fixAllMaterials(model.scene);
          // Mark as cache-owned so per-actor teardown never disposes shared model resources.
          this.markModelOwned(model);
        }
        return model;
      } catch (error) {
        console.error(`Failed to load model from ${path}: ${error}`);
        return null;
      }
    })();
  }

  /**
   * Loads a model from a URL.
   *
   * @param path - The path of the model to load
   * @param applyMaterialOverrides - Whether to apply material overrides to the model
   * @returns The loaded model, or null if the model could not be loaded
   */
  public async loadModel(path: AssetPath, applyMaterialOverrides: boolean = true): Promise<GLTF | null> {
    if (isNode() || !path.initialPath) {
      return null;
    }
    await MeshoptDecoder.ready;

    if (applyMaterialOverrides) {
      return this.loadModelWithMaterialsApplied(path);
    }
    else {
      return this.loadRawModel(path);
    }
  }

  /**
   * Deeply clone a GLTF, including its scene graph and animations.
   *
   * @param gltf - The original GLTF object from GLTFLoader.
   * @returns A new GLTF object with independent clones of the scene, scenes, and cameras.
   */
  private CloneModel(gltf: GLTF): GLTF {
    const clone: GLTF = {
      animations: gltf.animations.map((clip) => clip.clone ? clip.clone() : clip),
      scene: SkeletonUtils.clone(gltf.scene) as THREE.Group,
      scenes: gltf.scenes.map((scene) => SkeletonUtils.clone(scene) as THREE.Group),
      cameras: gltf.cameras ? gltf.cameras.map((cam) => cam.clone()) : [],
      asset: { ...gltf.asset },
      parser: gltf.parser,
      userData: { ...gltf.userData },
    };

    return clone;
  }


  /**
   * Loads a generic material from a URL or returns the material if it is already loaded.
   *
   * @param material - The material to load
   * @returns The loaded material, or null if the material could not be loaded
   */
  public async loadGenericMaterial(material: GenericMaterialType): Promise<THREE.Material | THREE.Material[] | null> {
    if (typeof material === 'string') {
      if (!material) {
        return null;
      }
      return this.loadMaterial(AssetPath.fromString(material));
    }

    if (material instanceof THREE.Material) {
      return material;
    }

    const normalizedMaterials: THREE.Material[] = [];
    for (const rawMaterial of material as unknown[]) {
      if (rawMaterial instanceof THREE.Material) {
        normalizedMaterials.push(rawMaterial);
        continue;
      }
      if (typeof rawMaterial === 'string' && rawMaterial.length > 0) {
        const loadedMaterial = await this.loadMaterial(AssetPath.fromString(rawMaterial));
        if (loadedMaterial) {
          normalizedMaterials.push(loadedMaterial);
        }
      }
    }

    return normalizedMaterials.length > 0 ? normalizedMaterials : null;
  }

  /**
   * Loads a material from a URL.
   *
   * @param path - The path of the material to load
   * @returns The loaded material, or null if the material could not be loaded
   */
  public async loadMaterial(path: AssetPath): Promise<THREE.Material | null> {
    // Skip material loading on server (materials reference textures)
    if (NetRuntime.isDedicatedServer()) {
      console.log(`[ResourceManager] Skipping material load on server: ${path}`);
      return null;
    }

    const rawUrl = path.initialPath;
    // Return existing promise if already cached
    if (rawUrl in this.cachedMaterials) {
      return this.cachedMaterials[rawUrl];
    }

    // Create and cache the loading promise
    const loadingPromise = (async () => {
      try {
        const json = await this.loadJsonObject(path);
        if (!json) {
          console.error(`Failed to download material from ${path}`);
          return null;
        }
        const loadedObject = WorldSerializer.importObject(json);
        if (!(loadedObject instanceof THREE.Material)) {
          console.error(`Object loaded from ${path} is not a material: ${loadedObject}`);
          return null;
        }
        const loadedMaterial = loadedObject as THREE.Material;
        MaterialUtils.fixMaterialColorSpace(loadedMaterial);
        this.markMaterialOwned(loadedMaterial);
        return loadedMaterial;
      } catch (error) {
        console.error(`Failed to load material from ${path}: ${error}`);
        return null;
      }
    })();

    this.cachedMaterials[rawUrl] = loadingPromise;

    return loadingPromise;
  }

  /**
   * Loads a sound from a URL.
   *
   * @param path - The path of the sound to load
   * @returns The loaded sound buffer, or null if the sound could not be loaded
   */
  public async loadSound(path: AssetPath): Promise<AudioBuffer | null> {
    const rawUrl = path.initialPath;

    if (!(rawUrl in this.cachedSounds)) {
      const loadingPromise = (async () => {
        try {
          path = await resolvePath(path);
          let sound: AudioBuffer | null = null;

          // TODO: This commented out code mirrors the code in loadModel. The reason
          // we cannot use it, is because there is no audio loader for a buffer. I don't
          // understand what the storageProvider.downloadFileAsBuffer path is for, so I cannot
          // say whether we need to use it or not.

          // ------------------------------------------------------------------------------------------------
          // if (isNode() && pathlib.isAbsolute(rawUrl)) {
          //   const storageProvider = projectInfo.storageProvider;
          //   if (!storageProvider) {
          //     throw new Error('No storage provider found');
          //   }
          //   const arrayBuffer = await storageProvider.downloadFileAsBuffer(path);
          //   sound = await this.audioLoader.loadAsync(arrayBuffer);
          // }
          // else {
          //   sound = await this.audioLoader.loadAsync(path.getResolvedPath());
          // }

          sound = await this.audioLoader.loadAsync(path.getResolvedPath());

          return sound;
        } catch (error) {
          console.error(`Failed to load sound from ${path}: ${error}`);
          return null;
        }
      })();
      this.cachedSounds[rawUrl] = loadingPromise;
    }
    return this.cachedSounds[rawUrl];
  }

  /**
   * Loads a json object from a URL.
   *
   * @param path - The path of the json object to load
   * @returns The loaded json object, or null if the file could not be loaded
   */
  public async loadJsonObject(path: AssetPath): Promise<any | null> {
    const rawUrl = path.initialPath;
    // Return existing promise if already cached
    if (rawUrl in this.cachedJsonObjects) {
      return this.cachedJsonObjects[rawUrl];
    }

    // Create and cache the loading promise
    const loadingPromise = (async () => {
      try {
        const json = await projectInfo.storageProvider?.downloadFileAsJson<any>(path);
        if (!json) {
          console.error(`Failed to download json object from ${path}`);
          return null;
        }
        return json;
      } catch (error) {
        console.error(`Failed to load json object from ${path}: ${error}`);
        return null;
      }
    })();

    this.cachedJsonObjects[rawUrl] = loadingPromise;

    return loadingPromise;
  }

  /**
   * Loads a resource from a resource file (e.g., .res.json, .material.json, .shader.json).
   * The file extension is determined by the resourcePrefix decorator option on the resource class.
   * Uses WorldSerializer to deserialize the resource instance (same as materials).
   *
   * @param path - The path of the resource to load
   * @returns The loaded resource data, or null if the resource could not be loaded
   */
  public async loadResource<T extends Resource = Resource>(path: AssetPath): Promise<ResourceData<T> | null> {
    const rawUrl = path.initialPath;

    if (rawUrl in this.cachedResources) {
      return this.cachedResources[rawUrl] as Promise<ResourceData<T> | null>;
    }

    const loadingPromise = (async () => {
      try {
        const json = await this.loadJsonObject(path);
        if (!json) {
          console.error(`Failed to download resource from ${path}`);
          return null;
        }

        // Use WorldSerializer to import the resource instance (just like materials)
        const instance = WorldSerializer.importObject(json) as T;

        // Check if this is a resource class (via metadata or inheritance)
        const isResource = ClassRegistry.isResourceClass(instance.constructor as any) ||
                          instance instanceof Resource;

        if (!isResource) {
          console.error(`Object loaded from ${path} is not a Resource: ${instance}`);
          return null;
        }

        // Extract class name from the instance
        const className = (instance.constructor as any).__className ?? instance.constructor.name;

        return {
          class: className,
          instance
        };
      } catch (error) {
        console.error(`Failed to load resource from ${path}: ${error}`);
        return null;
      }
    })();

    this.cachedResources[rawUrl] = loadingPromise;
    return loadingPromise as Promise<ResourceData<T> | null>;
  }

  /**
   * Create a new resource data structure for a given class.
   * Creates a default instance of the resource class.
   *
   * @param className - The full class name (e.g., ENGINE.MyResource or just MyResource)
   * @returns The resource data with defaults, or null if creation failed
   */
  public createResourceData<T extends Resource = Resource>(className: string): ResourceData<T> | null {
    try {
      // Resolve class name with proper prefix
      let resolvedClassName = className;
      let ResourceClass = ClassRegistry.getClass(resolvedClassName);

      if (!ResourceClass && !resolvedClassName.includes('.')) {
        resolvedClassName = `${Prefix.ENGINE}.${className}`;
        ResourceClass = ClassRegistry.getClass(resolvedClassName);

        if (!ResourceClass) {
          resolvedClassName = `${Prefix.GAME}.${className}`;
          ResourceClass = ClassRegistry.getClass(resolvedClassName);
        }
      }

      if (!ResourceClass) {
        console.error(`Resource class ${className} not found in ClassRegistry`);
        return null;
      }

      // Create instance with defaults
      const instance = ClassRegistry.constructObject(resolvedClassName) as T;

      if (!instance) {
        console.error(`Failed to construct instance of ${resolvedClassName}`);
        return null;
      }

      return {
        class: resolvedClassName,
        instance
      };
    } catch (error) {
      console.error(`Failed to create resource data for ${className}: ${error}`);
      return null;
    }
  }
}

export let resourceManager: ResourceManager = new ResourceManager();

setUrlTextureLoader((url) => resourceManager.loadTexture(AssetPath.fromString(url)));

export function setResourceManager(newResourceManager: ResourceManager): void {
  resourceManager = newResourceManager;
  console.log('[ResourceManager] set resource manager to', ClassRegistry.getClassOrConstructorName(newResourceManager));
}
